import { useEffect, useRef } from "react";

export function Modal({ isOpen, onClose, onConfirm, title, message, confirmText, cancelText, variant = "default", children }) {
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const variantStyles = {
    default: "border-brand-400",
    danger: "border-red-500",
    success: "border-green-500",
  };

  const confirmButtonStyles = {
    default: "border-brand-400 text-brand-400 hover:bg-brand-400/20",
    danger: "border-red-500 text-red-500 hover:bg-red-500/20",
    success: "border-green-500 text-green-500 hover:bg-green-500/20",
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-[20000]"
      style={{ background: "rgba(0, 0, 0, 0.6)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className={`rounded-xl px-7 py-6 text-center max-w-[400px] min-w-[280px] border ${variantStyles[variant]}`}
        style={{
          background: "rgba(25, 25, 25, 0.95)",
          backdropFilter: "blur(16px)",
        }}
      >
        {title && (
          <h3 className="text-white font-bold text-lg mb-3">{title}</h3>
        )}
        <p className="text-white text-sm leading-relaxed mb-5">{message}</p>
        {children}
        <div className="flex justify-center gap-3">
          {cancelText && (
            <button
              onClick={onClose}
              className="px-6 py-2 rounded-lg border border-white/20 bg-white/[0.08] text-white text-sm cursor-pointer transition-colors hover:bg-white/[0.15]"
            >
              {cancelText}
            </button>
          )}
          <button
            onClick={onConfirm || onClose}
            className={`px-6 py-2 rounded-lg border bg-white/[0.12] text-sm cursor-pointer transition-colors ${confirmButtonStyles[variant]}`}
          >
            {confirmText || "OK"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function PromptModal({ isOpen, onClose, onConfirm, message, confirmText, cancelText, placeholder }) {
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 100);
    }
  }, [isOpen]);

  const handleSubmit = () => {
    const value = inputRef.current?.value?.trim();
    if (value) onConfirm(value);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSubmit();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={handleSubmit}
      message={message}
      confirmText={confirmText || "OK"}
      cancelText={cancelText || "Cancelar"}
      variant="default"
    >
      <input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        onKeyDown={handleKeyDown}
        className="w-full mb-5 px-4 py-2 rounded-lg border border-white/20 text-white text-sm outline-none focus:border-brand-400 transition-colors"
        style={{
          background: "rgba(255, 255, 255, 0.1)",
        }}
      />
    </Modal>
  );
}
